import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  FormHelperText,
  Grid,
  Modal,
  Typography,
  useTheme,
} from "@mui/material";
import CategoryField from "app/components/FieldComponent/Category";
import FormField from "app/components/FieldComponent/FormField";
import OrganizationField from "app/components/FieldComponent/Organization";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  selectDailyActivityId,
  selectDuplicateDailyActivityId,
  selectForm,
  selectLoading,
} from "../../redux/selector";
import { executeErrorValidation } from "../../redux/validator";
import { UploadedFileCell } from "app/components/UploadedFileCell";
import { Resource, UploadedResource } from "../../types";
import { AudioImageModal } from "../AudioImageModal";
import { ResourcePreview } from "../ResourcePreview";
import { UploadField } from "../UploadField";
import { actions } from "./../../redux/slice";
import { useStyles } from "./styles";

type Props = {};

const DailyActivityAddModal = (props: Props) => {
  const form = useSelector(selectForm);
  const isLoading = useSelector(selectLoading);
  const styles = useStyles();
  const direction = useTheme().direction;

  const { t, i18n } = useTranslation();

  const dailyActivityId = useSelector(selectDailyActivityId);
  const duplicateDailyActivityId = useSelector(selectDuplicateDailyActivityId);

  const openModal = Number(dailyActivityId) > -1;
  const isEdit = Number(dailyActivityId) > 0 && !duplicateDailyActivityId;

  const dispatch = useDispatch();

  useEffect(() => {
    if (duplicateDailyActivityId) {
      return;
    }
    dispatch(actions.resetForm());
    if (dailyActivityId && !duplicateDailyActivityId) {
      dispatch(actions.getById({ id: dailyActivityId }));
    }
  }, [dailyActivityId, duplicateDailyActivityId]);

  function onUploadSuccess(resource: UploadedResource) {
    dispatch(actions.updateFormUploadValue({ resource }));
  }

  const handleDailyActivitySave = () => {
    const errors = executeErrorValidation(form);
    if (errors.length) {
      dispatch(actions.setFormErrors({ errors }));
    } else {
      dispatch(actions.createOrUpdate({ form: form }));
    }
  };

  const handleModalClose = () => {
    dispatch(actions.toggleModal({ id: undefined }));
  };

  const onChange = (key: string, value: string | number) => {
    dispatch(actions.setFormErrors({ errors: [{ name: key, error: "" }] }));
    dispatch(actions.updateFormValue({ key: key, value: value }));
  };

  const uploadedFiles = form.resources
    .filter((f) => !f.isDeleted && f.resourcePath)
    .slice(0, 1);

  const audioResource: Resource | undefined = uploadedFiles.find(
    (f) => f.resourceType === "audio"
  );

  const [audioModalOpen, setAudioModalOpen] = useState(false);

  const handleSaveAudioImage = (value: {
    thumbnailPath?: string;
    thumbnailName?: string;
  }) => {
    dispatch(actions.setAudioThumbnail(value));
  };

  const isBusy = isLoading || form.isLoading;

  return (
    <Modal open={openModal} onClose={handleModalClose}>
      <Card className={styles.addModal} sx={{ direction }}>
        <div className={styles.modalContainer}>
          <Grid>
            <Typography align="center" className={styles.headerModal}>
              {isEdit
                ? t("DAILY_ACTIVITY.EDIT_DAILY_ACTIVITY")
                : t("DAILY_ACTIVITY.ADD_DAILY_ACTIVITY")}
            </Typography>
          </Grid>

          <Grid container spacing={2} className={styles.bodyContainer}>
            <Grid item xs={12} md={8} className={styles.leftColumn}>
              <Grid container columnSpacing={2} rowSpacing={1}>
                <OrganizationField
                  value={form.organizationId.value}
                  xs={12}
                  changeValue={(value) => onChange("organizationId", value)}
                  disabled={isLoading}
                  showAll={false}
                  activeRequired={true}
                  error={form.organizationId.error}
                />

                <FormField
                  type="text"
                  xs={6}
                  required
                  label={t("DAILY_ACTIVITY.DAILY_ACTIVITY_NAME")}
                  placeholder={t("DAILY_ACTIVITY.ENTER_NAME")}
                  value={form.name.value}
                  disabled={isLoading}
                  error={form.name.error}
                  onChange={(v) => onChange("name", v)}
                />

                <FormField
                  type="number"
                  xs={4}
                  required
                  label={`#${t("DAILY_ACTIVITY.DAY_OF_ACTIVATION")}`}
                  placeholder={t("DAILY_ACTIVITY.ENTER_NUMBER")}
                  value={form.activationDayNumber.value}
                  disabled={isLoading}
                  error={form.activationDayNumber.error}
                  onChange={(v) => onChange("activationDayNumber", v)}
                />

                <FormField
                  type="time"
                  xs={2}
                  label={t("DAILY_ACTIVITY.TIME")}
                  value={form.scheduledAt.value}
                  disabled={isLoading}
                  error={form.scheduledAt.error}
                  onChange={(v) => onChange("scheduledAt", v)}
                />

                <FormField
                  type="text"
                  xs={12}
                  required
                  label={t("DAILY_ACTIVITY.SHORT_DESCRIPTION")}
                  placeholder={t("DAILY_ACTIVITY.ENTER_SHORT_DESCRIPTION")}
                  value={form.shortDescription.value}
                  disabled={isLoading}
                  error={form.shortDescription.error}
                  onChange={(v) => onChange("shortDescription", v)}
                />

                <FormField
                  type="textarea"
                  xs={12}
                  rows={4}
                  label={t("DAILY_ACTIVITY.DAILY_ACTIVITY_DESC")}
                  placeholder={t("DAILY_ACTIVITY.ENTER_DESCRIPTION")}
                  value={form.description.value}
                  disabled={isLoading}
                  error={form.description.error}
                  onChange={(v) => onChange("description", v)}
                />
              </Grid>
            </Grid>

            <Grid item xs="auto" className={styles.dividerWrap}>
              <Divider
                orientation="vertical"
                flexItem
                className={styles.divider}
              />
            </Grid>

            <Grid item xs={12} md={true} className={styles.rightColumn}>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Grid container>
                  <CategoryField
                    value={form.category.value}
                    xs={12}
                    changeValue={(value) => onChange("category", value)}
                    disabled={isLoading}
                    error={form.category.error}
                  />
                </Grid>

                <Box className={styles.uploadsRight}>
                  {uploadedFiles.length ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      {uploadedFiles.map((f) => (
                        <UploadedFileCell
                          className={styles.uploadedFileCell}
                          key={`${f.resourceType}-${f.resourcePath}`}
                          resources={[f]}
                          onDelete={() =>
                            dispatch(
                              actions.removeFormUploadValue({ resource: f })
                            )
                          }
                        />
                      ))}
                    </Box>
                  ) : (
                    <UploadField onUpload={onUploadSuccess} />
                  )}
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <label className={styles.label}>
                    {t("DAILY_ACTIVITY.UPLOADED_FILE")}
                  </label>
                  <Box className={styles.uploadedPanel}>
                    {uploadedFiles.length ? (
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          display: "flex",
                          gap: 1,
                          width: "100%",
                        }}
                      >
                        {uploadedFiles.map((f) => (
                          <Box
                            key={`${f.resourceType}-${f.resourcePath}`}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              minHeight: 0,
                              display: "flex",
                            }}
                          >
                            <ResourcePreview
                              resource={f}
                              onEditImage={
                                f.resourceType === "audio"
                                  ? () => setAudioModalOpen(true)
                                  : undefined
                              }
                            />
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography className={styles.noFile}>
                        {t("DAILY_ACTIVITY.NO_FILE_YET")}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ direction: i18n.dir(), width: "100%" }}>
                  {form.resourceError?.error ? (
                    <FormHelperText className={styles.uploadsError} error>
                      {form.resourceError?.error}
                    </FormHelperText>
                  ) : null}
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Grid
            container
            item
            xs={12}
            justifyContent={"center"}
            className={styles.actionsRow}
          >
            <Button
              className={styles.cancelBtn}
              variant="outlined"
              onClick={handleModalClose}
              disabled={isBusy}
            >
              {t("BUTTONS.CANCEL")}
            </Button>
            <Button
              className={styles.saveBtn}
              variant="contained"
              onClick={handleDailyActivitySave}
              disabled={isBusy}
            >
              {isEdit ? t("BUTTONS.SAVE") : t("DAILY_ACTIVITY.ADD")}
              {form.isLoading ? (
                <CircularProgress size={18} sx={{ marginLeft: 1 }} />
              ) : null}
            </Button>
          </Grid>
        </div>
        <AudioImageModal
          open={audioModalOpen}
          initialThumbnailPath={audioResource?.thumbnailPath}
          initialThumbnailName={audioResource?.thumbnailName}
          initialResourcePath={audioResource?.resourcePath}
          initialResourceUrl={audioResource?.url}
          onClose={() => setAudioModalOpen(false)}
          onSave={handleSaveAudioImage}
        />
      </Card>
    </Modal>
  );
};
export default DailyActivityAddModal;
